import { AfterViewInit, Component, ElementRef, OnDestroy, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';

type Step = 'idle' | 'hoverLink' | 'downloading' | 'downloadReady' | 'clickedFile';

@Component({
  selector: 'app-download-tutorial',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './download-tutorial.component.html',
  styleUrls: ['./download-tutorial.component.css'],
})
export class DownloadTutorialComponent implements AfterViewInit, OnDestroy {
  @ViewChild('stage', { static: true }) stageRef!: ElementRef<HTMLDivElement>;
  @ViewChild('downloadLink', { static: true }) linkRef!: ElementRef<HTMLElement>;
  @ViewChild('downloadItem', { static: false }) itemRef?: ElementRef<HTMLElement>;

  step: Step = 'idle';

  cursorX = 60;
  cursorY = 60;
  cursorDown = false;

  progress = 0; // 0..100
  private timers: number[] = [];
  private abort = false;

  fileName = 'AllCards.xlsx';

  ngAfterViewInit(): void {
    this.timers.push(window.setTimeout(() => this.loop(), 600));
  }

  ngOnDestroy(): void {
    this.abort = true;
    this.timers.forEach((t) => window.clearTimeout(t));
  }

  private loop() {
    if (this.abort) return;

    this.reset();

    this.timers.push(
      window.setTimeout(async () => {
        // Move to link + click
        this.step = 'hoverLink';
        await this.moveTo(this.linkRef.nativeElement, 520);
        await this.click(this.linkRef.nativeElement);

        // Show download bar + animate progress
        this.step = 'downloading';
        await this.animateProgress(1100);

        // Mark ready
        this.step = 'downloadReady';
        await this.wait(220);

        // Move to the downloaded item and click it
        // Wait one frame so the item exists in DOM
        await this.wait(60);
        const item = this.itemRef?.nativeElement;
        if (item) {
          await this.moveTo(item, 520);
          await this.click(item);
          this.step = 'clickedFile';
        }

        // Hold then restart
        this.timers.push(window.setTimeout(() => this.loop(), 6000));
      }, 250)
    );
  }

  private reset() {
    this.step = 'idle';
    this.progress = 0;
    this.cursorDown = false;
    this.cursorX = 70;
    this.cursorY = 90;
  }

  // Cursor movement in stage coordinates
  private async moveTo(targetEl: HTMLElement, duration = 520) {
    const stage = this.stageRef.nativeElement;
    const stageRect = stage.getBoundingClientRect();
    const r = targetEl.getBoundingClientRect();

    // Aim slightly inside the element
    const tx = r.left - stageRect.left + Math.min(30, r.width * 0.25);
    const ty = r.top - stageRect.top + r.height / 2;

    await this.tweenCursor(tx, ty, duration);
  }

  private async click(el: HTMLElement) {
    this.cursorDown = true;
    await this.wait(90);
    // We don’t actually trigger a real download here; we simulate it visually
    this.cursorDown = false;
    await this.wait(140);
  }

  private async animateProgress(durationMs: number) {
    const start = performance.now();
    return new Promise<void>((resolve) => {
      const tick = (now: number) => {
        if (this.abort) return resolve();
        const t = Math.min(1, (now - start) / durationMs);
        // easeOutCubic
        const e = 1 - Math.pow(1 - t, 3);
        this.progress = Math.round(e * 100);
        if (t < 1) requestAnimationFrame(tick);
        else resolve();
      };
      requestAnimationFrame(tick);
    });
  }

  private tweenCursor(tx: number, ty: number, duration: number) {
    const sx = this.cursorX;
    const sy = this.cursorY;
    const start = performance.now();

    return new Promise<void>((resolve) => {
      const step = (now: number) => {
        if (this.abort) return resolve();
        const t = Math.min(1, (now - start) / duration);
        const e = 1 - Math.pow(1 - t, 3);
        this.cursorX = sx + (tx - sx) * e;
        this.cursorY = sy + (ty - sy) * e;

        if (t < 1) requestAnimationFrame(step);
        else resolve();
      };
      requestAnimationFrame(step);
    });
  }

  private wait(ms: number) {
    return new Promise<void>((resolve) => {
      this.timers.push(window.setTimeout(() => resolve(), ms));
    });
  }
}
